// oxlint-disable react/only-export-components
import { Slot } from "radix-ui"
import { cva, type VariantProps } from "class-variance-authority"

import { type ButtonHTMLAttributes } from "react"
import { cn } from "cn"

const buttonVariants = cva<ButtonVariant>(
  "ring-offset-background inline-flex items-center justify-center rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-neutrals-500 focus-visible:ring-offset-2 focus-visible:outline-hidden",
  {
    variants: {
      variant: {
        default:
          "bg-primary-blue-600 font-semibold text-white hover:bg-primary-blue-700 disabled:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-neutrals-300",
        secondary:
          "bg-[#545e83] font-semibold text-white hover:bg-[#000e46] disabled:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-neutrals-300",
        subtle:
          "bg-neutrals-300 font-semibold text-neutrals-900 hover:bg-neutrals-400 disabled:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-neutrals-300",
        destructive:
          "bg-primary-red-400 text-white hover:bg-primary-red-600 disabled:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-neutrals-300",
        outline:
          "border border-primary-blue-600 bg-inherit font-semibold text-primary-blue-600 hover:bg-primary-blue-600 hover:text-white disabled:border-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-inherit",
        ghost:
          "font-semibold hover:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-inherit",
        "ghost-primary":
          "font-semibold text-primary-blue-600 hover:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-inherit",
        "ghost-destructive":
          "font-semibold text-primary-red-400 hover:bg-neutrals-200 hover:text-primary-red-500 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-inherit",
        link: "font-semibold text-black underline-offset-4 hover:underline disabled:text-neutrals-700 disabled:underline disabled:hover:cursor-not-allowed",
        "outline-secondary":
          "border border-neutrals-400 bg-inherit font-semibold text-neutrals-900 hover:bg-neutrals-300 disabled:border-neutrals-400 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-inherit",
        icon: "font-semibold hover:bg-neutrals-300 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed disabled:hover:bg-inherit",
        circle: "rounded-full bg-neutrals-200 p-2 hover:bg-neutrals-300",
      },
      size: {
        default: "h-10 px-3 py-2",
        xs: "h-6 w-6",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        circle: "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean
  size?: Size
  variant?: Variant
}

export type Size = "default" | "xs" | "sm" | "lg" | "icon" | "circle"

export type Variant =
  | "default"
  | "secondary"
  | "subtle"
  | "outline"
  | "outline-secondary"
  | "destructive"
  | "ghost"
  | "ghost-primary"
  | "ghost-destructive"
  | "link"
  | "icon"
  | "circle"

export type ButtonVariant = {
  variant: Record<Variant, string>
  size: Record<Size, string>
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
